import React, { useContext, useState } from 'react';
import { BookingContext } from '../context/BookingContext';
import axios from 'axios';
import shortid from 'shortid';
import { usePaystackPayment, PaystackButton, PaystackConsumer } from 'react-paystack';
import Receipt from './Receipt';
import SpaceList from './SpaceList';

const generateRandomNumber = () => {
  return Math.floor(100000 + Math.random() * 900000);
};

const BookingForm = () => {
  const { selectedDate, setSelectedDate, startTime, setStartTime, endTime, setEndTime, numPlayers, setNumPlayers } = useContext(BookingContext);
  const [availabilityStatus, setAvailabilityStatus] = useState('');
  const [timeRangeError, setTimeRangeError] = useState('');
  const [dateError, setDateError] = useState('');
  const [otherError, setOtherError] = useState('');
  const [contactError, setContactError] = useState('');

  const [showSummary, setShowSummary] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({});

  // Add states for email and phone number
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');


  const [loading, setLoading] = useState(false)
  const UserToken = localStorage.getItem("token");
  const [shortId, setUUid] = useState('')

  // Generate time options in hourly intervals from 08:00 to 22:00
  const generateHourOptions = () => {
    const hours = [];
    for (let i = 8; i <= 22; i++) {
      const hour = i < 10 ? `0${i}:00` : `${i}:00`;
      hours.push(hour);
    }
    return hours;
  };

  const clearState = () => {
    setSelectedDate('');  // Clear selected date
    setStartTime('');     // Clear start time
    setEndTime('');       // Clear end time
    setNumPlayers(8);     // Reset number of players to default (8 in your case)
  };

  // Filter end time options based on selected start time
  const generateEndTimeOptions = () => {
    if (!startTime) return generateHourOptions(); // Show all options if no start time is selected
    const startHour = parseInt(startTime.split(":")[0], 10);
    return generateHourOptions().filter((time) => parseInt(time.split(":")[0], 10) > startHour);
  };

  // Validate the time range (ensure no more than 2 hours difference)
  const validateTimeRange = (startTime, endTime) => {

    // console.log({startTime},{endTime})
    if(startTime === '' || endTime === '' || startTime === null || endTime === null){
      setTimeRangeError('Time range required')
      return
    }

    const startHour = parseInt(startTime.split(":")[0], 10);
    const endHour = parseInt(endTime.split(":")[0], 10);
    if (endHour - startHour > 2) {
      setTimeRangeError('You can only book a maximum of 2 hours.');
      return false;
    }
    setTimeRangeError('');
    return true;
  };

  // Validate the date (check if the date is in the future)
  const validateDate = (date) => {

    if(date === null){
      setDateError('Date required')
      return
    }

    const today = new Date().toISOString().split('T')[0];
    if (date < today) {
      setDateError('Please select a future date.');
      return false;
    }
    setDateError('');
    return true;
  };

  // Check availability with backend API
  const checkAvailability = async (e) => {
      e.preventDefault();

    if (!validateDate(selectedDate)) return;

    if (numPlayers < 4) {
      setOtherError('Min of 8 players');
      setShowSummary(false)
      return
    }

    if (email === '' || phoneNumber === '') {
      setContactError('Email and Phone number required')
      setShowSummary(false)
      return
    }

    setOtherError('')
    setContactError('')

    if (validateTimeRange(startTime, endTime)) {
      try {
        const response = await axios.post('https://adrenalinehub-v1.onrender.com/api/v1/booking/check-availability', {
          date: selectedDate,
          startTime,
          endTime,
        });

        if (response.data.available) {
          setAvailabilityStatus('Available');
          setShowSummary(true);  // Show the summary if available
          setUUid(generateRandomNumber())
        } else {
          setAvailabilityStatus('Unavailable, please select a different time.');
          setShowSummary(false); // Hide the summary if unavailable
        }
      } catch (error) {
        console.log(error);
        setAvailabilityStatus('Error checking availability.');
      }
    }
  };

  const calculatePrice = () => {
    if (!startTime || !endTime || !numPlayers) return 0;

    

    const [startHours, startMinutes] = startTime?.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);

    const startInMinutes = startHours * 60 + startMinutes;
    const endInMinutes = endHours * 60 + endMinutes;
    const durationInHours = (endInMinutes - startInMinutes) / 60;

    const hourlyRate = 1000;
    const effectivePlayers = numPlayers > 8 ? numPlayers - 1 : numPlayers; // Apply discount if more than 8 players
    const totalPrice = Math.max(durationInHours, 0) * hourlyRate * effectivePlayers;

    return totalPrice;
  };


  const config = {
    reference: shortId + '_' + (new Date()).getTime().toString(),
    email: email || '',
    amount: calculatePrice() * 100, //Amount is in the country's lowest currency. E.g Kobo, so 20000 kobo = N200
    // publicKey: process.env.Paystack_Live_Key,
    publicKey: 'pk_live_4f1ad904781210dac44ae6fdf07fcd3fa17f6be5',
    // publicKey: 'pk_test_f7982d7869e4143dc08d0ffbbd9dee564e5df9d7',
  };

  // const initializePayment = usePaystackPayment(config);

  // you can call this function anything
  const onSuccess = (reference) => {
    // Implementation for whatever you want to do with reference and after success call.
    // console.log({ reference });
    transactionCallback(reference)
  };

  // you can call this function anything
  const onClose = () => {
    // implementation for  whatever you want to do when the Paystack dialog closed.
    setLoading(false)
    // console.log('closed')
  }

  const componentProps = {
    ...config,
    text: 'Pay',
    onSuccess: (reference) => onSuccess(reference),
    onClose: onClose,
  };

  const transactionCallback = async (response) => {
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${UserToken}`);  // Add Authorization header if required

    // Construct the payload
    const payload = {
      transactionData: {
        ...response, // Ensure response contains all required data
        invoiceNo: shortId,  // Invoice number
        def_amount: calculatePrice(),  // The payment amount in smallest unit (e.g., kobo)
        created_at: new Date(),  // Timestamp for the transaction,
        selectedDate,
        startTime,
        endTime,
        name,
        numPlayers,
        email,
        phoneNumber
      },
      bookingId: shortId,  // The booking ID being processed

    };

    // Define the request options
    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify(payload),
      redirect: 'follow',
    };

    try {
      const result = await fetch(`${'https://adrenalinehub-v1.onrender.com'}/api/v1/transaction/pay`, requestOptions);
      const resultJson = await result.json();

      if (resultJson.status === 'ok') {
        setLoading(false);
        // clearState();
        // setShowSummary(false);
        // setEmail('');
        // setPhoneNumber('');
        // setAvailabilityStatus('');
        setPaymentSuccess(true);
        setBookingDetails({
          email,
          name,
          phoneNumber,
          bookingId:response.transaction,
          bookingDate: selectedDate,
          numberofPlayers: numPlayers,
          timeSlot: startTime + ' - ' + endTime,
          amountPaid: calculatePrice()
        })
        // console.log(resultJson.data);  // Log the updated booking data
      } else {
        console.error('Transaction failed:', resultJson);
        setLoading(false);
        // Handle error scenario (e.g., show a failure message)
      }
    } catch (error) {
      console.error('Error in transaction callback:', error);
      setLoading(false);
      // Handle fetch error scenario
    }
  };


  return (
    <div>
      {paymentSuccess ?
        <Receipt bookingDetails={bookingDetails}/>
        :
        <>
        <SpaceList/>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Select Date, Time Range, and Players</h2>
          <p className='text-red-500 mb-4'>N1,000 per player</p>
          <form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Date:</label>
              <input
                type="date"
                value={selectedDate || ''}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
              {dateError && <p className="mt-2 text-sm text-red-500">{dateError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">From:</label>
              <select
                value={startTime || ''}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setShowSummary(false)
                  setEndTime(""); // Reset end time when start time changes
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="" disabled>Select start time</option>
                {generateHourOptions().map((hour) => (
                  <option key={hour} value={hour}>{hour}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">To:</label>
              <select
                value={endTime || ''}
                onChange={(e) => {
                  setEndTime(e.target.value)
                  setShowSummary(false)
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                disabled={!startTime} // Disable if no start time is selected
              >
                <option value="" disabled>Select end time</option>
                {generateEndTimeOptions().map((hour) => (
                  <option key={hour} value={hour}>{hour}</option>
                ))}
              </select>
            </div>
            {timeRangeError && <p className="mt-2 text-sm text-red-500">{timeRangeError}</p>}

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Number of Players (min 4):</label>
              <input
                type="number"
                // min="8"
                value={numPlayers}
                onChange={(e) => setNumPlayers(parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required

              />
              {otherError && <p className="mt-2 text-sm text-red-500">{otherError}</p>}

            </div>


            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Name:</label>
              <input
                type="name"
                value={name || ''}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>
            {/* Email input */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email:</label>
              <input
                type="email"
                value={email || ''}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            {/* Phone number input */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Phone Number:</label>
              <input
                type="tel"
                value={phoneNumber || ''}
                maxLength={11}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
              {contactError && <p className="mt-2 text-sm text-red-500">{contactError}</p>}

            </div>

            <div>
              <button
                onClick={checkAvailability}
                className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 mt-4"
              >
                Check Availability
              </button>
            </div>

            {availabilityStatus && <p className="mt-2 text-sm text-red-500">{availabilityStatus}</p>}

            {/* Show booking summary when available */}
            {showSummary && (
              <div>
                <div className="mt-6 p-4 bg-gray-100 rounded-md">
                  <h3 className="text-lg font-semibold text-gray-800">Booking Summary</h3>
                  <ul className="mt-2">
                    <li><strong>Date:</strong> {selectedDate}</li>
                    <li><strong>Start Time:</strong> {startTime}</li>
                    <li><strong>End Time:</strong> {endTime}</li>
                    <li><strong>Number of Players:</strong> {numPlayers}</li>
                    <br />

                    <li><strong>name:</strong> {name}</li>
                    <li><strong>Email:</strong> {email}</li>
                    <li><strong>Phone</strong> {phoneNumber}</li>
                    <br />

                    <li><strong className="font-semibold">Total Price:</strong> ₦{calculatePrice()}</li>

                  </ul>
                  <p className="mt-4 text-green-600">Your selected time is available. You can now proceed to confirm and complete the booking!</p>
                </div>

                <PaystackConsumer {...componentProps} >
                  {({ initializePayment }) =>
                    <button disabled={loading} onClick={() => {
                      setLoading(true)
                      initializePayment(onSuccess, onClose)
                    }} className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-400 transition-all duration-200">
                      Confirm Booking
                    </button>

                  }
                </PaystackConsumer>

              </div>
            )}
          </form>
        </>
        }
    </div>
  );
};

export default BookingForm;
